import { HttpEvent, HttpEventType } from "@angular/common/http";
import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Guid, Logger } from "@se/se.ui.html5";
import { map } from "rxjs";
import { ButtonAction, FileView, JsonDataStructure, LoadedDataFromRoute, OriginPage, RowActionEvent, UploadStatus } from "../../models";
import { TooluploadService } from "../../toolupload.service";
import { HelperService } from "../../utils/helper.service";

@Component({
    selector: 'file-upload',
    templateUrl: "./file-upload.component.html",
    styleUrls: ["./file-upload.component.scss"]
})

export class FileUploadComponent implements OnInit, OnChanges {

    @Input() filesList: FileList;
    @Input() formData: FormGroup;
    @Input() loadedData : LoadedDataFromRoute;
    @Input() externalPage : OriginPage;

    filesView: Array<FileView>;
    loadSuccess: boolean;
    typeButton = ButtonAction;
    uploadProgress: number = 0;
    filesToUpload: number;

    private fileClickedIndex: number;
    private fileControlExtension = ['pdf'];
    private commonCorrelationId: string = null;

    showMultiUpload = false;

    constructor(
        private helper: HelperService,
        private logger: Logger,
        private tooluploadService: TooluploadService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.filesList.currentValue && this.filesList.length > 0) {
            for (let i = 0; i < this.filesList.length; i++) {
                const elem = this.filesList[i];
                this.filesView.push(this.convertInFileViewForTable(elem));
            }
            this.loadSuccess = undefined;
            this.uploadProgress = 0;
            this.filesToUpload = this.numberOfFilesToUpload();
        }
    }

    ngOnInit(): void {
        this.filesView = [];
    }

    private createReaderAndHttpCall(index: number, _: ButtonAction) {
        const reader = new FileReader();
        reader.addEventListener('load', (event) => {
            const result = event.target!.result;
            const fileToLoad = (result! as string).split(',')[1];
            const formToString = this.convertFormToString(index);
            const uploader = this.tooluploadService.uploader(fileToLoad, formToString)
                .pipe(
                    map((event: HttpEvent<any>) => {
                        return this.getHttpEventProgress(event);
                    })
                );

            uploader.subscribe({
                next: (progressPercentage: number) => {
                    this.filesView[index].progress = progressPercentage;
                    if (this.loadSuccess) {
                        this.addStatusSuccess(index);
                    }
                },
                error: (error) => {
                    this.loadSuccess = false;
                    this.addStatusFailed(index);
                    this.logger.error(error);
                }
            })
        });
        reader.readAsDataURL(this.filesView[index].file);
    }

    private getHttpEventProgress(event: HttpEvent<any>): number {
        switch (event.type) {
            case HttpEventType.UploadProgress:
                const percentDone = event.total ? Math.round(100 * event.loaded / event.total) : 0;
                this.uploadProgress = percentDone;
                return percentDone;
            case HttpEventType.Response:
                this.loadSuccess = true;
                this.uploadProgress = 100;
                return this.uploadProgress;
            default:
                this.loadSuccess = null;
                this.uploadProgress = 0;
                return this.uploadProgress;
        }
    }

    private convertInFileViewForTable(singleFile: File): FileView {
        return {
            name: singleFile.name,
            size: singleFile.size,
            type: singleFile.type,
            progress: 0,
            status: this.manageStatusFileText(singleFile),
            disableAllButtons: false,
            disableUploadButton: this.disableUploadButton(singleFile),
            file: singleFile
        };

    }

    private transformByteToMega(size: number): number {
        return size / 1024 / 1024;
    }

    private manageStatusFileText(singleFile: File): string {
        return this.isFileOversize(singleFile) ? UploadStatus.TOO_LARGE :
            (!this.isTypeFileSupported(singleFile) ? UploadStatus.NOT_SUPPORTED : '');
    }

    private isFileOversize(singleFile: File): boolean {
        return this.transformByteToMega(singleFile.size) > 2048;
    }

    private isTypeFileSupported(singleFile: File): boolean {
        const fileName = singleFile.name;
        let fileTypeDotExtension = fileName.lastIndexOf('.');
        const extensionFile = fileName.substring(fileTypeDotExtension);
        return this.loadedData.acceptedFormats.includes(extensionFile);
    }

    private disableUploadButton(singleFile: File): boolean {
        if (this.isFileOversize(singleFile)) {
            return true;
        } else {
            return !this.isTypeFileSupported(singleFile);
        }
    }

    private addStatusSuccess(rowIndex: number) {
        if (this.loadSuccess && this.filesView[rowIndex].status === '') {
            this.filesView[rowIndex].status = UploadStatus.UPLOADED;
            this.filesView[rowIndex].disableAllButtons = true;
        }
        this.loadSuccess = null;
        this.filesToUpload = this.numberOfFilesToUpload();
    }

    private addStatusFailed(rowIndex: number) {
        if (!this.loadSuccess && this.filesView[rowIndex].status === '') {
            this.filesView[rowIndex].status = UploadStatus.ERROR_UPLOAD;
            this.filesView[rowIndex].disableAllButtons = false;
        }
        this.filesToUpload = this.numberOfFilesToUpload();
    }

    multipleActionAllowed(): boolean {
        return this.filesView.some(el => el.status === '');
    }

    uploadAll() {
        if (this.multipleActionAllowed()) {
            for (let [i, element] of this.filesView.entries()) {
                if (element.status === '') {
                    this.createReaderAndHttpCall(i, ButtonAction.UPLOAD_ALL);
                }
            }
            this.filesToUpload = this.numberOfFilesToUpload();
        }
    }

    deleteAll() {
        if (this.multipleActionAllowed()) {
            for (let [i, element] of this.filesView.entries()) {
                if (element.status === '') {
                    this.filesView.splice(i);
                }
            }
            this.filesToUpload = this.numberOfFilesToUpload();
        }
    }

    rowClicked(index: number, event: RowActionEvent) {
        this.fileClickedIndex = index;
        switch (event.action) {
            case (ButtonAction.UPLOAD):
                this.createReaderAndHttpCall(this.fileClickedIndex, event.action);
                this.filesToUpload = 1;
                break;
            case (ButtonAction.DELETE):
                this.filesView.splice(this.fileClickedIndex, 1);
                if (this.filesView.length === 0) {
                    this.filesView = [];
                    this.uploadProgress = 0;
                }
                this.filesToUpload = this.numberOfFilesToUpload();
                break;
            default:
                throw new Error(event + ' Errore click su riga ');
        }
    }

    numberOfFilesToUpload() {
        return this.filesView.filter(el => el.status === '').length;
    }

    private setNewCommonCorrelationId(): string {
        this.commonCorrelationId = new Guid().toString();
        return this.commonCorrelationId;
    }

    private addCorrelationId(template: JsonDataStructure): string {
        if (this.fileControlExtension.includes(template.DocumentApproved.Format)) {
            template.CorrelationId = new Guid().toString();
        } else {
            template.CorrelationId = this.commonCorrelationId || this.setNewCommonCorrelationId();
        }
        return JSON.stringify(template);
    }

    private convertFormToString(index: number): string {
        let template = this.helper.createBody(this.filesView[index].file, index, this.formData, this.loadedData);
        return this.addCorrelationId(template);
    }
}
